export const parseCsvLine = (line: string): string[] => {
  const res: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      res.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  res.push(cur);
  return res.map(s => s.trim());
};

const validateSubscriberCsvFile = async (
  file: File,
  requiredHeaders: string[]
): Promise<{ valid: boolean; errors: string[] }> => {
  const errors: string[] = [];
  const text = await file.text();
  const rawLines = text.split(/\r?\n/).map(l => l.replace(/\r$/, ''));
  const lines = rawLines.filter(l => l.trim() !== '');

  if (lines.length < 2) {
    errors.push('CSV file must contain headers and at least one data row');
    return { valid: false, errors };
  }

  const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase());
  for (const required of requiredHeaders) {
    if (!headers.includes(required)) {
      errors.push(`Missing required column: ${required}`);
    }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  for (let i = 1; i < lines.length; i++) {
    const rowNumber = i + 1;
    const values = parseCsvLine(lines[i]);

    if (values.length !== headers.length) {
      errors.push(`Row ${rowNumber}: Column count mismatch (expected ${headers.length}, got ${values.length})`);
      continue;
    }

    const rowData: any = {};
    headers.forEach((h, idx) => {
      rowData[h] = values[idx];
    });

    const missingRequiredHeaders = requiredHeaders.filter(header => !rowData[header]);
    if (missingRequiredHeaders.length > 0) {
      const missingFieldsMessage = missingRequiredHeaders.length === 1
        ? `${missingRequiredHeaders[0]} is required`
        : `${missingRequiredHeaders.join(' and ')} are required`;
      errors.push(`Row ${rowNumber}: ${missingFieldsMessage}`);
      continue;
    }

    if (!emailRegex.test(rowData.email)) {
      errors.push(`Row ${rowNumber}: Invalid email format ('${rowData.email}')`);
    }

    if (rowData.entrydate) {
      const parsed = Date.parse(rowData.entrydate);
      if (isNaN(parsed)) {
        errors.push(`Row ${rowNumber}: Invalid entryDate format ('${rowData.entrydate}')`);
      }
    }

    if (rowData.lastopened) {
      const parsed = Date.parse(rowData.lastopened);
      if (isNaN(parsed)) {
        errors.push(`Row ${rowNumber}: Invalid lastOpened format ('${rowData.lastopened}')`);
      }
    }

    if (rowData.optin) {
      const v = rowData.optin.toLowerCase();
      if (!(v === 'true' || v === 'false' || v === '1' || v === '0')) {
        errors.push(`Row ${rowNumber}: Invalid optIn value ('${rowData.optin}') - use true/false or 1/0`);
      }
    }

    if (errors.length >= 200) break;
  }

  return { valid: errors.length === 0, errors };
};

export const validateCsvFile = async (file: File): Promise<{ valid: boolean; errors: string[] }> => {
  return validateSubscriberCsvFile(file, ['email', 'source']);
};

export const validateUpdateCsvFile = async (file: File): Promise<{ valid: boolean; errors: string[] }> => {
  return validateSubscriberCsvFile(file, ['email']);
};

export const validateTagsCsvFile = async (file: File): Promise<{ valid: boolean; errors: string[]; validRows: string[]; duplicateCount: number }> => {
  const errors: string[] = [];
  const validRows: string[] = [];
  const text = await file.text();
  const rawLines = text.split(/\r?\n/).map(l => l.replace(/\r$/, ''));
  const lines = rawLines.filter(l => l.trim() !== '');

  if (lines.length < 2) {
    errors.push('CSV file must contain headers and at least one data row');
    return { valid: false, errors, validRows, duplicateCount: 0 };
  }

  const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase());
  const requiredHeaders = ['email', 'tags'];
  for (const required of requiredHeaders) {
    if (!headers.includes(required)) {
      errors.push(`Missing required column: ${required}`);
    }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const seenEmails = new Set<string>();
  let duplicateCount = 0;

  for (let i = 1; i < lines.length; i++) {
    const rowNumber = i + 1;
    const values = parseCsvLine(lines[i]);
    let rowHasError = false;

    if (values.length !== headers.length) {
      errors.push(`Row ${rowNumber}: Column count mismatch (expected ${headers.length}, got ${values.length})`);
      rowHasError = true;
    } else {
      const rowData: any = {};
      headers.forEach((h, idx) => {
        rowData[h] = values[idx];
      });

      if (!rowData.email) {
        errors.push(`Row ${rowNumber}: email is required`);
        rowHasError = true;
      } else if (!emailRegex.test(rowData.email)) {
        errors.push(`Row ${rowNumber}: Invalid email format ('${rowData.email}')`);
        rowHasError = true;
      } else if (seenEmails.has(rowData.email.toLowerCase())) {
        duplicateCount++;
        rowHasError = true; // Skip duplicate emails
      } else {
        seenEmails.add(rowData.email.toLowerCase());
      }

      if (!rowHasError && rowData.tags && rowData.tags.trim()) {
        const rawTags = rowData.tags.split('-').map((tag: string) => tag.trim()).filter((tag: string) => tag.length > 0);
        
        const invalidTags = rawTags.filter((tag: string) => !/^[a-zA-Z0-9_-]+$/.test(tag));
        if (invalidTags.length > 0) {
          errors.push(`Row ${rowNumber}: Invalid tag characters. Only alphanumeric, hyphens (-), and underscores (_) allowed: ${invalidTags.join(', ')}`);
          rowHasError = true;
        }

        const longTags = rawTags.filter((tag: string) => tag.length > 255);
        if (longTags.length > 0) {
          errors.push(`Row ${rowNumber}: Tag name too long (max 255 characters): ${longTags.join(', ')}`);
          rowHasError = true;
        }
      }
    }

    if (!rowHasError) {
      validRows.push(lines[i]);
    }

    if (errors.length >= 200) break;
  }

  return { valid: errors.length === 0, errors, validRows, duplicateCount };
};
